import { useCallback, useEffect } from 'react';
import {
    checkAndSavePendingClick,
    sendClickDataToAttributionService,
    storePendingClickData,
    storePendingClickInLocalStorage,
} from '../clickTracking';
import {
    deleteAttributionIDInLocalStorage,
    generateUUID,
    getAttributionIDFromLocalStorage,
    isServerSideTrackingEnabledForOrganization,
    setAttributionIDInLocalStorage,
} from '../utils/index';
import { useTranscendConsent } from './useTranscendConsent';

export interface ClickDataInput {
    organization_id: string;
    fbclid?: string;
    fbc?: string;
    fbp?: string;
    timestamp?: number;
}

export const useAttributionClickTracking = () => {
    const {
        canStoreAttributionData,
        isOptInJurisdiction,
        isConsentDecisionMade,
        isInitialized,
    } = useTranscendConsent();

    const sendClickData = useCallback(
        async (clickDataInput: ClickDataInput) => {
            if (
                !(await isServerSideTrackingEnabledForOrganization(
                    clickDataInput.organization_id,
                ))
            ) {
                return;
            }

            // Wait for consent state to be initialized
            if (!isInitialized) {
                return;
            }

            // Bake in stable_id and user_agent
            const stableId =
                getAttributionIDFromLocalStorage() || generateUUID();
            const userAgent =
                typeof navigator !== 'undefined' ? navigator.userAgent : '';

            if (!stableId) {
                console.log('No stable_id available');
                return;
            } else {
                setAttributionIDInLocalStorage(stableId);
            }

            const fullClickData = {
                ...clickDataInput,
                stable_id: stableId,
                user_agent: userAgent,
            };

            if (canStoreAttributionData) {
                sendClickDataToAttributionService(fullClickData);
                return;
            }

            // If in opt-in jurisdiction and consent explicitly denied, don't send
            if (
                isOptInJurisdiction &&
                isConsentDecisionMade &&
                !canStoreAttributionData
            ) {
                deleteAttributionIDInLocalStorage();

                return;
            }

            // If in opt-in jurisdiction and waiting for consent decision, store in pending table
            if (isOptInJurisdiction && !isConsentDecisionMade) {
                storePendingClickInLocalStorage(clickDataInput.organization_id);
                storePendingClickData(fullClickData); // Fire and forget
                return;
            }
        },
        [
            canStoreAttributionData,
            isOptInJurisdiction,
            isConsentDecisionMade,
            isInitialized,
        ],
    );

    // Real-time: Save pending click data when consent is granted on same page
    useEffect(() => {
        if (
            isOptInJurisdiction &&
            canStoreAttributionData &&
            isConsentDecisionMade
        ) {
            checkAndSavePendingClick(
                canStoreAttributionData,
                isOptInJurisdiction,
                isConsentDecisionMade,
            );
        }
    }, [canStoreAttributionData, isOptInJurisdiction, isConsentDecisionMade]);

    // Page load: Check for pending click data on every page load (fallback)
    useEffect(() => {
        checkAndSavePendingClick(
            canStoreAttributionData,
            isOptInJurisdiction,
            isConsentDecisionMade,
        );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Global event listener: Listen for Transcend consent changes across pages
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        const handleConsentChange = ({
            detail: { consent, oldConsent, changes },
        }: any) => {
            console.log('Transcend consent changed:', {
                consent,
                oldConsent,
                changes,
            });
            // Check if we need to save pending click when consent changes
            timeoutId = setTimeout(() => {
                checkAndSavePendingClick();
            }, 100); // Small delay to ensure consent state is updated
        };

        // Listen for Transcend consent changes
        if (typeof window !== 'undefined' && window.airgap?.addEventListener) {
            window.airgap.addEventListener(
                'consent-change',
                handleConsentChange,
            );
        }

        return () => {
            // Clear timeout if component unmounts
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (
                typeof window !== 'undefined' &&
                window.airgap?.removeEventListener
            ) {
                window.airgap.removeEventListener(
                    'consent-change',
                    handleConsentChange,
                );
            }
        };
    }, []);

    return { sendClickData };
};
